import { DateOnly } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import { ErrorAlert } from '@if/mui'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { useCalculateAmountToSpread } from '@src/api/loan/loan-api'
import MultiLineChip from '@src/components/Common/Chip/MultiLineChip'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUnmountableDialog } from '@src/services/dialog'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface LoanSpreadOverdueAmountDialogProps {
  initialSignedSimulation: SignedCalendarSimulation
  paymentDates: DateOnly[]
  onConfirm: (amountToAdd: number) => void
  onClose: () => void
}

const LoanSpreadOverdueAmountDialog = ({
  initialSignedSimulation,
  paymentDates,
  onConfirm,
  onClose,
}: LoanSpreadOverdueAmountDialogProps) => {
  const { t } = useTranslation()
  const {
    mutateAsync: calculateAmount,
    error,
    isPending,
  } = useCalculateAmountToSpread(
    initialSignedSimulation.simulation.fullLoan.loan.id,
    initialSignedSimulation.simulation.fullLoan.loan.financingProgramId,
  )

  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, false)
  const originalSimulation = initialSignedSimulation.simulation
  const [paymentValueToAdd, setPaymentValueToAdd] = useState<number>(0)
  const [newLastPaymentAmount, setNewLastPaymentAmount] = useState<number>(0)
  const [newLastPaymentDate, setNewLastPaymentDate] = useState<DateOnly>(new DateOnly())

  const originalLastPayment = useMemo(() => {
    const lastPayment = originalSimulation.customerPaymentOperations
      .filter((p) => p.operation.effectiveDate <= originalSimulation.fullLoan.loan.lastPaymentOn)
      .at(-1)
    return lastPayment!.operation
  }, [originalSimulation.customerPaymentOperations, originalSimulation.fullLoan.loan.lastPaymentOn])

  const paymentInformations = useMemo(
    () =>
      originalSimulation.operationInformations.filter((oi) =>
        paymentDates.some((date) => date.getTime() === oi.operation.effectiveDate.getTime()),
      ),
    [originalSimulation.operationInformations, paymentDates],
  )

  const getChipColor = (originalAmount: number, newAmount: number) => {
    if (originalAmount > newAmount) return 'success'
    if (originalAmount < newAmount) return 'error'
    return 'default'
  }

  useEffect(() => {
    // Use setTimeout to defer the API call to the next tick
    // This is to ensure that the form state is fully updated before making the API call
    const timeoutId = setTimeout(() => {
      void calculateAmount({
        paymentsDate: paymentInformations.map((oi) => oi.operation.effectiveDate),
        simulation: initialSignedSimulation.simulation,
      }).then((result) => {
        setPaymentValueToAdd(result.amountToRaise)
        setNewLastPaymentAmount(result.lastPaymentValue)
        setNewLastPaymentDate(result.lastPaymentDate)
      })
    }, 0)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInformations])

  const handleConfirm = () => {
    onConfirm(paymentValueToAdd)
    closeDialog()
  }

  return (
    <FormDialog
      {...dialogProps}
      dividers
      onConfirm={handleConfirm}
      title={
        <Typography align="center" variant="h6">
          {t('loan.payment.spreadOverdueAmountDialog.title')}
        </Typography>
      }
      disabled={isPending || paymentValueToAdd <= 0}
    >
      {error && <ErrorAlert error={error} />}
      <Stack>
        <Typography variant="subtitle1" align="center" component="div">
          {t('loan.payment.spreadOverdueAmountDialog.amountToAdd', { paymentNb: paymentDates.length })}
        </Typography>
        <Stack direction="row" alignSelf={'center'} margin={1}>
          <Chip label={formatCurrency(paymentValueToAdd)} size="small" color={'success'} />
        </Stack>
        <Typography align="center" margin={1}>
          {t('loan.payment.spreadOverdueAmountDialog.newLastPayment')}
        </Typography>
        <Stack direction="row" spacing={1} alignSelf={'center'} marginBottom={1}>
          <MultiLineChip
            label={`${originalLastPayment.effectiveDate.toString()}\n${formatCurrency(originalLastPayment.amount)}`}
            variant="outlined"
          />
          <Box paddingTop={1}>
            <ArrowForwardIcon fontSize="small" color="disabled" />
          </Box>
          <MultiLineChip
            label={`${newLastPaymentDate.toString()}\n${formatCurrency(newLastPaymentAmount)}`}
            color={getChipColor(originalLastPayment.amount, newLastPaymentAmount)}
          />
        </Stack>
      </Stack>
    </FormDialog>
  )
}

export default LoanSpreadOverdueAmountDialog
